import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";

// —— Types
export type Product = {
    id: string;
    slug: string; // e.g., "/products/green-tea"
    title: string;
    category: string;
    tags: string[];
    description: string;
    metaKeywords: string[]; // optional keywords/meta
    image: string;
    price: number;
    isBestSeller: boolean;
    isNew: boolean;
    stock: number;
    isPreOrder: boolean;
    tagline: string;
    ingredients: string[];
    uses: string[];
    benefits: string[];
};

export type SearchResult = Product & { __score: number; __matches: string[] };

export type SearchContextValue = {
    products: Product[];
    setProducts: React.Dispatch<React.SetStateAction<Product[]>>;

    // modal visibility state site‑wide
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;

    // current query shared globally (header, modal, page)
    query: string;
    setQuery: (q: string) => void;

    // results set site‑wide (computed in useSearch hook)
    results: SearchResult[];
    setResults: React.Dispatch<React.SetStateAction<SearchResult[]>>;

    // searching indicator
    isSearching: boolean;
    setIsSearching: React.Dispatch<React.SetStateAction<boolean>>;

    // optional: expose a simple navigate helper for product clicks
    onNavigateProduct?: (slug: string) => void;
};

export const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export type SearchProviderProps = {
    children: React.ReactNode;
    // If you already have product data at runtime, pass it here.
    initialProducts?: Product[];
    // If provided, used for navigation from results
    onNavigateProduct?: (slug: string) => void;
    // Endpoint for lazy JSON loading (recommended to generate at build):
    indexUrl?: string; // default "/search/index.json"
};

export const SearchProvider: React.FC<SearchProviderProps> = ({
    children,
    initialProducts = [],
    onNavigateProduct,
    indexUrl = "/search/index.json",
}) => {
    const [products, setProducts] = useState<Product[]>(initialProducts);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const fetchedRef = useRef(false);

    // Lazy-load index if not provided
    useEffect(() => {
        if (products.length || fetchedRef.current) return;
        fetchedRef.current = true;

        if (typeof window === "undefined") return; // SSR guard

        fetch(indexUrl)
            .then((res) => (res.ok ? res.json() : []))
            .then((json) => {
                if (Array.isArray(json)) {
                    // Expecting array of Product
                    setProducts(json as Product[]);
                } else if (Array.isArray(json?.items)) {
                    setProducts(json.items as Product[]);
                }
            })
            .catch(() => {
                // Silent fail; developer can provide initialProducts instead.
            });
    }, [indexUrl, products.length]);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((v) => !v), []);

    const value = useMemo(
        () => ({
            products,
            setProducts,
            isOpen,
            open,
            close,
            toggle,
            query,
            setQuery,
            results,
            setResults,
            isSearching,
            setIsSearching,
            onNavigateProduct,
        }),
        [products, isOpen, query, results, isSearching, onNavigateProduct],
    );

    return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};
